import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Skeleton,
} from '@mui/material';
import { BaseButton } from '../../base';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PageHeader from '../../base/PageHeader';
import DashboardService, { DashboardStats } from '../../../../services/DashboardService';

const ACCENT = '#005e5d';
const SURFACE_SECONDARY = '#F2F3F5';
const BORDER = '#E6E7EB';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';

/** Mini Sparkline SVG Component */
const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({ 
  data, 
  color, 
  height = 32 
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 100 / (data.length - 1);
  const points = data
    .map((v, i) => `${i * w},${height - ((v - min) / range) * (height - 4) - 2}`)
    .join(' ');

  const gradientId = `sg-${color.replace('#', '')}`;

  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline 
        points={`${points} 100,${height} 0,${height}`}
        fill={`url(#${gradientId})`} 
        stroke="none" 
      />
      <polyline 
        points={points} 
        fill="none" 
        stroke={color} 
        strokeWidth="1.5"
        strokeLinecap="round" 
        strokeLinejoin="round" 
        vectorEffect="non-scaling-stroke" 
      />
    </svg>
  );
};

/** Stat Card with Sparkline */
const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  trendUp?: boolean;
  sparkData?: number[];
}> = ({ title, value, icon, color, trend, trendUp = true, sparkData = [] }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: '14px',
        border: `1px solid ${BORDER}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderColor: `${color}55`,
          boxShadow: `0 4px 20px ${color}15`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ color, fontSize: '18px', display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        </Box>
        {trend && (
          <Chip
            size="small"
            label={trend}
            icon={trendUp ? <ArrowUpIcon sx={{ fontSize: '12px !important' }} /> : <ArrowDownIcon sx={{ fontSize: '12px !important' }} />}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: trendUp ? '#49CC9015' : '#F93E3E15',
              color: trendUp ? '#49CC90' : '#F93E3E',
              fontFamily: '"Fira Code", monospace',
              '& .MuiChip-icon': { ml: 0.5, mr: -0.3 },
            }}
          />
        )}
      </Box>

      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          lineHeight: 1.1,
          mb: 0.3,
          color: TEXT_PRIMARY,
        }}
      >
        {value}
      </Typography>

      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500 }}>
        {title}
      </Typography>

      {sparkData.length > 0 && (
        <Box sx={{ mt: 1.5, mx: -0.5 }}>
          <Sparkline data={sparkData} color={color} height={30} />
        </Box>
      )}
    </Paper>
  );
};

/** Simple Bar Chart Replacement using LinearProgress */
const BarChart: React.FC<{
  title: string;
  subtitle: string;
  data: { label: string; value: number }[];
  total?: number;
}> = ({ title, subtitle, data, total }) => {
  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: `1px solid ${BORDER}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: TEXT_PRIMARY }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, mt: 0.2 }}>
            {subtitle}
          </Typography>
        </Box>
        {total && (
          <Chip size="small" label={`${total.toLocaleString()} total`} sx={{ height: 22, fontSize: 10, fontWeight: 600 }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {data.map((item, i) => (
          <Box key={i}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace', color: TEXT_SECONDARY }}>
                {item.value.toLocaleString()}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(item.value / maxValue) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: SURFACE_SECONDARY,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: ACCENT,
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const CARD_PALETTE = [
  { color: '#61AFFE', icon: '👥' },
  { color: '#49CC90', icon: '🖼️' },
  { color: '#FCA130', icon: '📄' },
  { color: '#A78BFA', icon: '💬' },
  { color: '#005e5d', icon: '📚' },
  { color: '#F93E3E', icon: '📦' },
];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const statusColor = (code: number) => {
  if (code < 300) return '#49CC90';
  if (code < 400) return '#61AFFE';
  if (code < 500) return '#FCA130';
  return '#F93E3E';
};

// ── Main component ────────────────────────────────────────────────────────────

export const DashboardPage: React.FC = () => {
  const [data, setData] = React.useState<DashboardStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    DashboardService.getStats()
      .then(stats => { setData(stats); })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // ── Build stat cards from collections ─────────────────────────────────────
  const statCards = React.useMemo(() => {
    if (!data) return [];
    return data.collections.slice(0, 6).map((col, i) => {
      const palette = CARD_PALETTE[i % CARD_PALETTE.length];
      const trend = col.newThisMonth > 0
        ? `+${col.newThisMonth} this month`
        : 'no new this month';
      return {
        title: col.label,
        value: col.count.toLocaleString(),
        icon: palette.icon,
        color: palette.color,
        trend,
        trendUp: col.newThisMonth > 0,
        sparkData: [] as number[],
      };
    });
  }, [data]);

  // ── Volume bar chart ──────────────────────────────────────────────────────
  const volumeData = React.useMemo(() => {
    if (!data) return [];
    return data.weeklyActivity.map(d => ({ label: d.day, value: d.count }));
  }, [data]);

  const totalVolume = volumeData.reduce((s, d) => s + d.value, 0);

  // ── Status distribution ───────────────────────────────────────────────────
  const statusData = data?.statusDistribution ?? [];
  const totalStatus = statusData.reduce((s, d) => s + d.count, 0);

  // ── Top endpoints ─────────────────────────────────────────────────────────
  const topEndpoints = React.useMemo(() => {
    if (!data) return [];
    return data.topEndpoints.map(ep => ({
      path: ep.path,
      requests: ep.count,
      avg: ep.avgMs,
    }));
  }, [data]);

  // ── Recent logs ───────────────────────────────────────────────────────────
  const recentActivity = React.useMemo(() => {
    if (!data) return [];
    return data.recentLogs.map((log, i) => ({
      id: i,
      method: log.method,
      path: log.path,
      status: log.statusCode,
      time: log.responseTime,
      ago: relativeTime(log.createdAt),
    }));
  }, [data]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        <PageHeader title="Dashboard" subtitle="Overview of your platform performance and activity" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0,1,2,3].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={130} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={260} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={260} /></Grid>
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your platform performance and activity"
      />

      {/* Header Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <BaseButton
          size="small"
          variant="outlined"
          colorVariant="secondary"
          startIcon={<RefreshIcon sx={{ fontSize: '14px !important' }} />}
          onClick={load}
        >
          Refresh
        </BaseButton>
      </Box>

      {/* Requests summary chips */}
      {data && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${data.totalRequests.toLocaleString()} total API requests`}
            sx={{ fontFamily: '"Fira Code", monospace', fontSize: 11 }} />
          <Chip size="small" label={`${data.totalRequestsToday.toLocaleString()} today`}
            sx={{ fontFamily: '"Fira Code", monospace', fontSize: 11, bgcolor: '#005e5d15', color: '#005e5d' }} />
        </Box>
      )}

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <StatCard {...card} />
          </Grid>
        ))}
        {statCards.length === 0 && (
          <Grid item xs={12}>
            <Typography sx={{ color: TEXT_TERTIARY, fontSize: 13, p: 2 }}>
              No collection data available yet.
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Charts Row 1: Volume + Status */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <BarChart
            title="API Activity"
            subtitle="Requests per day — last 7 days"
            data={volumeData}
            total={totalVolume}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', mb: 0.5, color: TEXT_PRIMARY }}>
              Status Distribution
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, mb: 2 }}>
              Response codes breakdown
            </Typography>
            {statusData.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: TEXT_TERTIARY }}>No request data yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {statusData.map((item, i) => (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: item.color }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1, color: TEXT_PRIMARY }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, fontFamily: '"Fira Code", monospace' }}>
                        {item.rate}%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={item.rate}
                      sx={{ height: 4, borderRadius: 2, backgroundColor: SURFACE_SECONDARY,
                        '& .MuiLinearProgress-bar': { backgroundColor: item.color } }} />
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>Total</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
                    {totalStatus.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2: Top Endpoints + Avg Response */}
      {topEndpoints.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: `1px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', mb: 0.5, color: TEXT_PRIMARY }}>
                Top Endpoints
              </Typography>
              <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, mb: 2 }}>
                Most accessed routes (all time)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {topEndpoints.map((ep, i) => {
                  const maxReqs = topEndpoints[0].requests;
                  return (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography noWrap
                          sx={{ fontSize: 12, fontWeight: 600, fontFamily: '"Fira Code", monospace',
                            flex: 1, minWidth: 0, color: TEXT_PRIMARY }}>
                          {ep.path}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, fontFamily: '"Fira Code", monospace' }}>
                          {ep.requests.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ position: 'relative', height: 6, borderRadius: 3,
                        backgroundColor: SURFACE_SECONDARY, overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%',
                          width: `${(ep.requests / maxReqs) * 100}%`, borderRadius: 3,
                          backgroundColor: ACCENT, transition: 'width 0.6s cubic-bezier(0.22,1,0.36,1)' }} />
                      </Box>
                      <Typography sx={{ fontSize: 10, color: TEXT_TERTIARY, mt: 0.3 }}>
                        avg {ep.avg}ms
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <BarChart
              title="Avg Response Time"
              subtitle="Milliseconds per endpoint"
              data={topEndpoints.map(ep => ({ label: ep.path, value: ep.avg }))}
            />
          </Grid>
        </Grid>
      )}

      {/* Recent Activity Table */}
      <Paper elevation={0} sx={{ borderRadius: '14px', border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}` }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: TEXT_PRIMARY }}>
              Recent API Requests
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_TERTIARY, mt: 0.2 }}>
              Last 20 logged requests
            </Typography>
          </Box>
        </Box>
        {recentActivity.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: TEXT_TERTIARY }}>
              No request logs yet. Logs will appear after API activity.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: SURFACE_SECONDARY }}>
                  {['Method', 'Endpoint', 'Status', 'Time', 'When'].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', color: TEXT_TERTIARY,
                      display: h === 'When' || h === 'Time' ? { xs: 'none', sm: 'table-cell' } : undefined }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recentActivity.map((row, i) => (
                  <TableRow key={i} sx={{ '&:hover': { backgroundColor: SURFACE_SECONDARY },
                    '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Chip size="small" label={row.method}
                        sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: '#005e5d18', color: '#005e5d',
                          fontFamily: '"Fira Code", monospace' }} />
                    </TableCell>
                    <TableCell sx={{ color: TEXT_PRIMARY }}>
                      <Typography noWrap sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace',
                        fontWeight: 500, maxWidth: 280 }}>
                        {row.path}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status}
                        sx={{ height: 20, fontSize: 11, fontWeight: 700,
                          bgcolor: `${statusColor(row.status)}18`,
                          color: statusColor(row.status),
                          fontFamily: '"Fira Code", monospace' }} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: TEXT_SECONDARY }}>
                      <Typography sx={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
                        {row.time}ms
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: TEXT_TERTIARY }}>
                      <Typography sx={{ fontSize: 12 }}>{row.ago}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardPage;
